@if (isInitialPageLoading()) {
  <div center-layout [fullHeight]="true" padding="lg">
    <div stack-layout gap="sm" alignItems="center">
      <Text role="headline-small" color="on-background-variant">
        Loading RMA configuration...
      </Text>
      <Text role="body-medium" color="on-background-variant">
        Please wait while we fetch channel and user details.
      </Text>
    </div>
  </div>
} @else {
<div stack-layout [fullHeight]="true" gap="md" paddingVertical="md">

  <!-- Back link -->
  <div box-layout paddingHorizontal="md">
    <button
      text-button
      size="md"
      symbolLeft="chevron_left"
      variant="ghost"
      [routerLink]="['/locations/warehouses', warehouseCode]"
    >
      {{ warehouseName() }} Details
    </button>
  </div>

  <div center-layout paddingHorizontal="lg">
    <div stack-layout gap="md" [fullWidth]="true">

      <!-- Page header row: title + channel selector -->
      <div row-layout justifyContent="space-between" alignItems="flex-start">
        <div stack-layout gap="xs">
          <Text role="title-large" color="on-background-variant">RMA Configuration</Text>
          <Text role="body-medium" color="on-background-variant">
            Configure functional permissions for this channel.
          </Text>
        </div>

        <div row-layout alignItems="center" gap="md">
          <Text role="headline-small" color="on-background-variant">Channel</Text>
          <app-select
            [form]="channelForm"
            controlName="channelCode"
            [options]="salesChannelOptions()"
            placeholder="Select channel"
          />
        </div>
      </div>

      <!-- Main card -->
      <div [class]="cardClass">

        <!-- Card section header -->
        <div [class]="sectionHeaderClass">
          <Text role="headline-small" color="on-background-variant">Assign RMA Configuration</Text>
        </div>

        <div stack-layout padding="md" gap="md">

          <!-- RMA Settings -->
          <Text role="headline-medium" color="on-background-variant">RMA Settings</Text>

          <div grid-layout templateColumns="42% 1fr" >
            <div row-layout  gap="lg" alignItems="center" wrap="wrap" paddingVertical="sm">
              <!-- Enable Channel for RMA's -->
              <div row-layout [grow]="1" justifyContent="flex-start" alignItems="center" gap="xl">
                <div stack-layout gap="xs">
                  <Text role="headline-small" color="on-background-variant">Enable Channel for RMA's</Text>
                  <Text role="body-small" color="on-background-variant">
                    Allow this channel to create and manage RMAs.
                  </Text>
                </div>
                <div row-layout alignItems="center" gap="sm">
                  <label [class]="toggleClass">
                    <input
                      type="checkbox"
                      [formControl]="$any(rmaSettingsForm.get('enableChannelForRma'))"
                    />
                    <span></span>
                  </label>
                  <Text role="body-medium" color="on-background-variant">
                    {{ rmaSettingsForm.get('enableChannelForRma')?.value ? 'Yes' : 'No' }}
                  </Text>
                </div>
              </div>

              <!-- Auto Approve RMAs -->
              <div row-layout [grow]="1" justifyContent="flex-start" alignItems="center" gap="xl">
                <div stack-layout gap="xs">
                  <Text role="headline-small" color="on-background-variant">Auto Approve RMAs</Text>
                  <Text role="body-small" color="on-background-variant">Automatically</Text>
                </div>
                <div row-layout alignItems="center" gap="sm">
                  <Text role="body-medium" color="on-background-variant">
                    {{ rmaSettingsForm.get('autoApproveRma')?.value ? 'Yes' : 'No' }}
                  </Text>
                  <label [class]="toggleClass">
                    <input
                      type="checkbox"
                      [formControl]="$any(rmaSettingsForm.get('autoApproveRma'))"
                    />
                    <span></span>
                  </label>
                </div>
              </div>
            </div>

            <div></div>
          </div>

          <hr [class]="dividerClass" />

          <!-- RMA Creators table -->
          <Text role="headline-medium" color="on-background-variant">RMA Creators</Text>

          <table [class]="tableClass">
            <thead>
              <tr>
                <th><Text role="headline-small">Email <span style="color:#ED2121">*</span></Text></th>
                <th><Text role="headline-small">First Name</Text></th>
                <th><Text role="headline-small">Last Name</Text></th>
                <th><Text role="headline-small">Action</Text></th>
              </tr>
            </thead>
            <tbody>
              @for (creator of creators; track $index; let rowIndex = $index) {
                <tr>
                  <td>
                    <div [class]="emailFieldClass">
                      <input
                        [class]="emailInputClass"
                        type="email"
                        autocomplete="off"
                        placeholder="email@example.com"
                        [formControl]="$any(creator.get('email'))"
                        (input)="onCreatorEmailInput($index)"
                        (focus)="onCreatorEmailFocus($index)"
                        (blur)="onCreatorEmailBlur($index)"
                      />
                      @if (
                        isCreatorSuggestionsOpen($index) &&
                        (shouldShowCreatorMinCharHint($index) ||
                          isCreatorSearchLoading($index) ||
                          creatorSuggestions($index).length > 0 ||
                          shouldShowNoCreatorResults($index))
                      ) {
                        <div [class]="suggestionsDropdownClass">
                          @if (shouldShowCreatorMinCharHint($index)) {
                            <div [class]="suggestionStatusClass">
                              Please enter at least {{ minSearchLength }} characters to search.
                            </div>
                          } @else if (isCreatorSearchLoading($index)) {
                            <div [class]="suggestionStatusClass">Searching…</div>
                          } @else if (creatorSuggestions($index).length > 0) {
                            @for (user of creatorSuggestions($index); track user.email) {
                              <button
                                type="button"
                                [class]="suggestionItemClass"
                                (mousedown)="selectCreatorSuggestion(rowIndex, user)"
                              >
                                <span [class]="suggestionEmailClass">{{ user.email }}</span>
                                <span [class]="suggestionNameClass">
                                  {{ user.firstName }} {{ user.lastName }}
                                </span>
                              </button>
                            }
                          } @else if (shouldShowNoCreatorResults($index)) {
                            <div [class]="suggestionStatusClass">No users found</div>
                          }
                        </div>
                      }
                      @if (
                        creator.get('email')?.hasError('required') &&
                        creator.get('email')?.touched
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          Email is required.
                        </Text>
                      } @else if (
                        creator.get('email')?.hasError('email') &&
                        (creator.get('email')?.dirty || creator.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          Please enter a valid email address.
                        </Text>
                      } @else if (
                        creator.get('email')?.hasError('duplicateCreator') &&
                        (creator.get('email')?.dirty || creator.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          This creator has already been added.
                        </Text>
                      } @else if (
                        creator.get('email')?.hasError('alreadyApprover') &&
                        (creator.get('email')?.dirty || creator.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          A user can be either an approver or creator, not both.
                        </Text>
                      }
                    </div>
                  </td>
                  <td>
                    <Text role="body-medium">{{ creator.get('firstName')?.value }}</Text>
                  </td>
                  <td>
                    <Text role="body-medium">{{ creator.get('lastName')?.value }}</Text>
                  </td>
                  <td>
                    <button [class]="deleteButtonClass" type="button" (click)="removeCreator($index)">
                      <Symbol name="delete" />
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>

          <div>
            <button [class]="addApproverButtonClass" type="button" (click)="addCreator()">
              + Add RMA's Creators
            </button>
          </div>

          <hr [class]="dividerClass" />

          <!-- RMA Approvers table -->
          <Text role="headline-medium" color="on-background-variant">RMA Approvers</Text>

          <table [class]="tableClass">
            <thead>
              <tr>
                <th><Text role="headline-small">Email <span style="color:#ED2121">*</span></Text></th>
                <th><Text role="headline-small">First Name</Text></th>
                <th><Text role="headline-small">Last Name</Text></th>
                <th><Text role="headline-small">Action</Text></th>
              </tr>
            </thead>
            <tbody>
              @for (approver of approvers; track $index; let rowIndex = $index) {
                <tr>
                  <td>
                    <div [class]="emailFieldClass">
                      <input
                        [class]="emailInputClass"
                        type="email"
                        autocomplete="off"
                        placeholder="email@example.com"
                        [formControl]="$any(approver.get('email'))"
                        (input)="onApproverEmailInput($index)"
                        (focus)="onApproverEmailFocus($index)"
                        (blur)="onApproverEmailBlur($index)"
                      />
                      @if (
                        isSuggestionsOpen($index) &&
                        (shouldShowMinCharHint($index) ||
                          isApproverSearchLoading($index) ||
                          approverSuggestions($index).length > 0 ||
                          shouldShowNoApproverResults($index))
                      ) {
                        <div [class]="suggestionsDropdownClass">
                          @if (shouldShowMinCharHint($index)) {
                            <div [class]="suggestionStatusClass">
                              Please enter at least {{ minSearchLength }} characters to search.
                            </div>
                          } @else if (isApproverSearchLoading($index)) {
                            <div [class]="suggestionStatusClass">Searching…</div>
                          } @else if (approverSuggestions($index).length > 0) {
                            @for (user of approverSuggestions($index); track user.email) {
                              <button
                                type="button"
                                [class]="suggestionItemClass"
                                (mousedown)="selectApproverSuggestion(rowIndex, user)"
                              >
                                <span [class]="suggestionEmailClass">{{ user.email }}</span>
                                <span [class]="suggestionNameClass">
                                  {{ user.firstName }} {{ user.lastName }}
                                </span>
                              </button>
                            }
                          } @else if (shouldShowNoApproverResults($index)) {
                            <div [class]="suggestionStatusClass">No users found</div>
                          }
                        </div>
                      }
                      @if (
                        approver.get('email')?.hasError('required') &&
                        approver.get('email')?.touched
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          Email is required.
                        </Text>
                      } @else if (
                        approver.get('email')?.hasError('email') &&
                        (approver.get('email')?.dirty || approver.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          Please enter a valid email address.
                        </Text>
                      } @else if (
                        approver.get('email')?.hasError('duplicateApprover') &&
                        (approver.get('email')?.dirty || approver.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          This approver has already been added.
                        </Text>
                      } @else if (
                        approver.get('email')?.hasError('alreadyCreator') &&
                        (approver.get('email')?.dirty || approver.get('email')?.touched)
                      ) {
                        <Text role="body-small" style="color:#ED2121">
                          A user can be either an approver or creator, not both.
                        </Text>
                      }
                    </div>
                  </td>
                  <td>
                    <Text role="body-medium">{{ approver.get('firstName')?.value }}</Text>
                  </td>
                  <td>
                    <Text role="body-medium">{{ approver.get('lastName')?.value }}</Text>
                  </td>
                  <td>
                    <button [class]="deleteButtonClass" type="button" (click)="removeApprover($index)">
                      <Symbol name="delete" />
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>

          <div>
            <button [class]="addApproverButtonClass" type="button" (click)="addApprover()">
              + Add RMA's Approvers
            </button>
          </div>

          <hr [class]="dividerClass" />

          <!-- RMA Approver types checkboxes -->
          <div grid-layout templateColumns="42% 1fr">
            <div row-layout alignItems="center" gap="xl" paddingVertical="sm" wrap="wrap">
              <Text role="headline-medium" color="on-background-variant">RMA Types</Text>

              <div row-layout [grow]="1" alignItems="center" gap="lg" wrap="wrap">
                <div>
                  <app-checkbox
                    [form]="rmaSettingsForm"
                    controlName="isRegular"
                    label="Regular"
                    labelPosition="before"
                  />
                </div>
                <div>
                  <app-checkbox
                    [form]="rmaSettingsForm"
                    controlName="isCreditOnly"
                    label="Credit Only"
                    labelPosition="before"
                  />
                </div>
              </div>
            </div>

            <div></div>
          </div>

        </div>
      </div>

      <!-- Footer actions -->
      <div row-layout justifyContent="flex-end" gap="sm" paddingBottom="md">
        <button
          text-button
          size="md"
          variant="warn"
          (click)="cancel()"
        >
          Cancel
        </button>
        <button
          text-button
          size="md"
          variant="primary"
          [disabled]="isSaving"
          (click)="save()"
        >
          Save
        </button>
      </div>

    </div>
  </div>
</div>
}